/**
 * Pure CMS domain logic shared by the public pages, the content API, and the
 * admin editor. No server imports — safe to use from route components.
 *
 * The content itself lives in the `cms_documents` and `announcements` tables
 * (see db/schema.ts); this module is the vocabulary around it: the document
 * kinds, the docs sidebar grouping, and the announcement visibility window.
 */

export const CMS_KINDS = ['post', 'guide', 'legal'] as const
export type CmsKind = (typeof CMS_KINDS)[number]

export const CMS_KIND_LABELS: Record<CmsKind, string> = {
  post: 'Blog post',
  guide: 'Docs guide',
  legal: 'Legal page',
}

/** Where each kind is served, for the admin editor's "view page" link. */
export function publicPathFor(kind: CmsKind, slug: string): string {
  if (kind === 'post') return `/blog/${slug}`
  if (kind === 'guide') return `/docs/${slug}`
  return `/${slug}`
}

/**
 * The shape every stored slug has (lowercase-alphanumeric-hyphen, <=64). Used
 * by the PUBLIC read paths to treat a malformed URL segment as "no such
 * document" (a 404) rather than a validation ERROR — crawlers and stale links
 * routinely produce uppercase/dotted/encoded slugs, and those should get the
 * not-found page, not the error boundary. The admin save path still uses the
 * throwing zod schema, where a violation IS an input error.
 */
export function isValidSlug(slug: unknown): slug is string {
  return typeof slug === 'string' && /^[a-z0-9-]{1,64}$/.test(slug)
}

/**
 * CMS row ids are uuids. Write surfaces validate the SHAPE before querying: a
 * non-uuid id cannot exist, and letting it reach Postgres raises a 22P02 cast
 * error (a 500) instead of the not-found the endpoint means. Lives here (pure,
 * client-safe) so both write surfaces build their schema from one regex.
 */
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Sidebar group order for the docs shell. A guide whose category isn't listed
 * here lands in a trailing "More" group rather than disappearing from the nav
 * (same contract the frontmatter-driven collection had).
 */
export const DOC_CATEGORIES = [
  'Getting started',
  'Integrations',
  'Account & billing',
  'Advanced',
]

/** The legal slugs that have a route; the admin editor can't create others. */
export const LEGAL_SLUGS = [
  'terms',
  'privacy',
  'acceptable-use',
  'cancellation',
] as const
export type LegalSlug = (typeof LEGAL_SLUGS)[number]

export interface GuideNavItem {
  slug: string
  title: string
  description: string
  category: string
}

export interface DocsNavGroup {
  category: string
  guides: GuideNavItem[]
}

/**
 * Group guides (already in reading order) for the docs sidebar and index, in
 * `DOC_CATEGORIES` order with unknown categories collected under "More".
 */
export function groupGuides(guides: GuideNavItem[]): DocsNavGroup[] {
  const known = DOC_CATEGORIES.map((category) => ({
    category,
    guides: guides.filter((g) => g.category === category),
  }))
  const leftovers = guides.filter((g) => !DOC_CATEGORIES.includes(g.category))
  if (leftovers.length > 0) known.push({ category: 'More', guides: leftovers })
  return known.filter((group) => group.guides.length > 0)
}

/** Format an ISO date (or date-time) as e.g. "July 1, 2026", in UTC. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export const ANNOUNCEMENT_LEVELS = ['info', 'warning', 'critical'] as const
export type AnnouncementLevel = (typeof ANNOUNCEMENT_LEVELS)[number]

export interface AnnouncementWindow {
  active: boolean
  /** ISO timestamps (or null for unbounded), as the API serializes them. */
  startsAt: string | null
  endsAt: string | null
}

/**
 * Whether a banner should show at `now`. The DB query filters `active` only;
 * this is the single definition of the time window, used by the server reads
 * and testable without a database.
 */
export function isAnnouncementLive(a: AnnouncementWindow, now: Date): boolean {
  if (!a.active) return false
  if (a.startsAt && now < new Date(a.startsAt)) return false
  if (a.endsAt && now >= new Date(a.endsAt)) return false
  return true
}

/** A parseable ISO timestamp WITH an explicit zone (datetime-local values
 *  arrive zoneless — the client must convert to UTC before sending). */
export function isIsoInstant(s: string): boolean {
  return !Number.isNaN(Date.parse(s)) && /[Zz]|[+-]\d\d:?\d\d$/.test(s)
}

/** An announcement window is ordered when either bound is open or end > start. */
export function isWindowOrdered(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): boolean {
  return !startsAt || !endsAt || new Date(endsAt) > new Date(startsAt)
}
